import {
  Container, Title, Text, Stack, Table, Badge, Button, Group, Loader, Alert,
  Tooltip, Card, Grid, ThemeIcon, Divider,
} from '@/components/ui';
import { ActionIcon } from '@mantine/core';
import {
  IconAlertCircle, IconEye, IconArrowLeft, IconHistory,
  IconCheck, IconPlayerPlay, IconX, IconClockHour4,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAiWorkflowService } from '@actschurch/ccms-sdk/react';
import { useEnrichedAuth } from '../../../../hooks/useEnrichedAuth';
import type { AiWorkflowSession, AiWorkflowStatus } from '@actschurch/ccms-sdk';

const statusColors: Record<AiWorkflowStatus, string> = {
  InProgress: 'blue',
  AwaitingConfirmation: 'yellow',
  Confirmed: 'cyan',
  Created: 'green',
  Failed: 'red',
  Expired: 'gray',
};

function formatDate(ts: number): string {
  if (!ts) return '-';
  // CCMS timestamp format: yyyyMMddHHmmss (e.g. 20260417143403)
  const s = String(ts);
  if (s.length === 14) {
    return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)} ${s.slice(8, 10)}:${s.slice(10, 12)}`;
  }
  return s;
}

export default function SessionsPage() {
  const navigate = useNavigate();
  const workflowService = useAiWorkflowService();
  const { user } = useEnrichedAuth();

  const { data: sessions, isLoading, error } = useQuery({
    queryKey: ['ai', 'workflow-sessions'],
    queryFn: () => workflowService.getMySessions(),
    enabled: !!user,
  });

  const totalSessions = sessions?.length ?? 0;
  const completedCount = sessions?.filter((s: AiWorkflowSession) => s.status === 'Created').length ?? 0;
  const inProgressCount = sessions?.filter((s: AiWorkflowSession) =>
    s.status === 'InProgress' || s.status === 'AwaitingConfirmation' || s.status === 'Confirmed'
  ).length ?? 0;
  const failedCount = sessions?.filter((s: AiWorkflowSession) => s.status === 'Failed').length ?? 0;

  return (
    <Container fluid py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" wrap="wrap">
          <div>
            <Title order={2}>AI Workflow Sessions</Title>
            <Text size="sm" c="dimmed">Your previous AI workflow sessions.</Text>
          </div>
          <Button
            variant="default"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate('/app/ai')}
          >
            Back to AI
          </Button>
        </Group>

        {/* Stat cards */}
        <Grid>
          <Grid.Col span={{ base: 6, md: 3 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group wrap="nowrap">
                <ThemeIcon size={48} radius="md" variant="light" color="blue">
                  <IconHistory size={24} />
                </ThemeIcon>
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Total Sessions</Text>
                  <Text size="xl" fw={700}>{totalSessions}</Text>
                </div>
              </Group>
            </Card>
          </Grid.Col>
          <Grid.Col span={{ base: 6, md: 3 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group wrap="nowrap">
                <ThemeIcon size={48} radius="md" variant="light" color="green">
                  <IconCheck size={24} />
                </ThemeIcon>
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Completed</Text>
                  <Text size="xl" fw={700}>{completedCount}</Text>
                </div>
              </Group>
            </Card>
          </Grid.Col>
          <Grid.Col span={{ base: 6, md: 3 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group wrap="nowrap">
                <ThemeIcon size={48} radius="md" variant="light" color="yellow">
                  <IconPlayerPlay size={24} />
                </ThemeIcon>
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>In Progress</Text>
                  <Text size="xl" fw={700}>{inProgressCount}</Text>
                </div>
              </Group>
            </Card>
          </Grid.Col>
          <Grid.Col span={{ base: 6, md: 3 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group wrap="nowrap">
                <ThemeIcon size={48} radius="md" variant="light" color="red">
                  <IconX size={24} />
                </ThemeIcon>
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Failed</Text>
                  <Text size="xl" fw={700}>{failedCount}</Text>
                </div>
              </Group>
            </Card>
          </Grid.Col>
        </Grid>

        {/* Loading state */}
        {isLoading && (
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Stack align="center" py="xl">
              <Loader size="md" />
              <Text c="dimmed">Loading sessions...</Text>
            </Stack>
          </Card>
        )}

        {/* Error state */}
        {error && (
          <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
            {error instanceof Error ? error.message : 'Failed to load sessions'}
          </Alert>
        )}

        {/* Empty state */}
        {sessions && sessions.length === 0 && (
          <Alert title="No sessions found" color="blue">
            You have not started any AI workflows yet.
          </Alert>
        )}

        {/* Sessions table in Card */}
        {sessions && sessions.length > 0 && (
          <Card shadow="sm" padding={0} radius="md" withBorder>
            <Group justify="space-between" p="lg" pb="sm">
              <Group gap="xs">
                <IconClockHour4 size={20} />
                <Title order={4}>Session History</Title>
              </Group>
              <Badge variant="light" color="blue">{sessions.length} sessions</Badge>
            </Group>
            <Divider />
            <Table tableVariant="striped">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>ID</Table.Th>
                  <Table.Th>Input Type</Table.Th>
                  <Table.Th>Target</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Record ID</Table.Th>
                  <Table.Th>Created</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {sessions.map((session: AiWorkflowSession) => (
                  <Table.Tr key={session.sessionID}>
                    <Table.Td>{session.sessionID}</Table.Td>
                    <Table.Td>
                      <Badge variant="light" size="sm">
                        {session.inputType}
                      </Badge>
                    </Table.Td>
                    <Table.Td>{session.targetRecordType}</Table.Td>
                    <Table.Td>
                      <Badge
                        variant="light"
                        color={statusColors[session.status] ?? 'gray'}
                        size="sm"
                      >
                        {session.status}
                      </Badge>
                    </Table.Td>
                    <Table.Td>{session.targetRecordID ?? '-'}</Table.Td>
                    <Table.Td>{formatDate(session.createdDateTime)}</Table.Td>
                    <Table.Td>
                      <Tooltip label="View details">
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          onClick={() => {
                            // Future: navigate to session detail page
                          }}
                        >
                          <IconEye size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Card>
        )}
      </Stack>
    </Container>
  );
}
